<template>
  <div class="ocr-report-document" :class="{ 'ocr-report-document--collapsed': collapsed }">
    <div class="ocr-report-document-head">
      <span class="ocr-report-document-title">Document Preview</span>
      <div class="ocr-report-document-actions">
        <v-btn size="x-small" variant="text" @click="toggleCollapsed">
          {{ collapsed ? "Show" : "Minimize" }}
        </v-btn>
        <v-btn v-if="objectUrl && !collapsed" size="x-small" variant="text" @click="reload">Reload</v-btn>
      </div>
    </div>
    <template v-if="!collapsed">
      <div v-if="loading" class="ocr-report-document-placeholder">Loading document…</div>
      <p v-else-if="error" class="ocr-report-document-error">{{ error }}</p>
      <div v-else-if="objectUrl && isPdf" ref="docBody" class="ocr-doc-body">
        <div v-if="showThumbs" class="ocr-doc-thumbs" :style="{ width: `${thumbWidth}px` }">
          <p v-if="thumbsLoading && !thumbs.length" class="ocr-doc-thumbs-status">Loading pages…</p>
          <button
            v-for="thumb in thumbs"
            :key="thumb.page"
            type="button"
            class="ocr-doc-thumb"
            :class="{ 'ocr-doc-thumb--active': thumb.page === pageIndex }"
            @click="$emit('update:pageIndex', thumb.page)"
          >
            <img :src="thumb.src" alt="" />
            <span>{{ thumb.page + 1 }}</span>
          </button>
        </div>
        <div
          v-if="showThumbs"
          class="ocr-split-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize page thumbnails"
          @pointerdown="onThumbDown"
          @pointermove="onThumbMove"
          @pointerup="onThumbUp"
          @pointercancel="onThumbUp"
        />
        <iframe
          class="ocr-report-document-frame"
          :key="pageIndex"
          :src="frameSrc"
          title="Report PDF preview"
        />
      </div>
      <p v-else-if="objectUrl" class="ocr-report-document-placeholder">
        Preview available for PDF only. File loaded as download in browser if opened directly.
      </p>
      <p v-else class="ocr-report-document-placeholder">Select a report to preview.</p>
    </template>
  </div>
</template>

<script>
import * as pdfjsLib from "pdfjs-dist";
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";

const pdfWorkerUrl = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url);
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl.toString();

export default {
  name: "OcrReportDocument",
  props: {
    filename: { type: String, default: null },
    pageIndex: { type: Number, default: 0 },
  },
  emits: ["update:collapsed", "update:pageIndex"],
  data() {
    return {
      loading: false,
      error: "",
      objectUrl: "",
      isPdf: false,
      collapsed: false,
      thumbs: [],
      thumbsLoading: false,
      thumbWidth: 120,
      thumbDrag: false,
      renderToken: 0,
      pdfDoc: null,
    };
  },
  computed: {
    frameSrc() {
      if (!this.objectUrl) return "";
      const page = Math.max(1, (this.pageIndex || 0) + 1);
      return `${this.objectUrl}#page=${page}&toolbar=1&navpanes=0&view=FitH`;
    },
    showThumbs() {
      return this.thumbsLoading || this.thumbs.length > 0;
    },
  },
  watch: {
    filename: {
      immediate: true,
      handler() {
        this.reload();
      },
    },
    pageIndex() {
      this.$nextTick(() => {
        this.$el.querySelector(".ocr-doc-thumb--active")?.scrollIntoView({ block: "nearest" });
      });
    },
  },
  beforeUnmount() {
    this.renderToken += 1;
    this.destroyPdf();
    this.revokeUrl();
  },
  methods: {
    toggleCollapsed() {
      this.collapsed = !this.collapsed;
      this.$emit("update:collapsed", this.collapsed);
    },
    onThumbDown(event) {
      if (event.button !== 0) return;
      this.thumbDrag = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onThumbMove(event) {
      if (!this.thumbDrag || !this.$refs.docBody) return;
      const left = this.$refs.docBody.getBoundingClientRect().left;
      this.thumbWidth = Math.min(280, Math.max(72, Math.round(event.clientX - left)));
    },
    onThumbUp() {
      this.thumbDrag = false;
    },
    destroyPdf() {
      const doc = this.pdfDoc;
      this.pdfDoc = null;
      if (doc) doc.destroy().catch(() => {});
    },
    revokeUrl() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = "";
      }
    },
    async renderThumbs(buffer) {
      const token = this.renderToken + 1;
      this.renderToken = token;
      this.destroyPdf();
      this.thumbs = [];
      this.thumbsLoading = true;
      let pdf = null;
      try {
        pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
        if (token !== this.renderToken) return;
        this.pdfDoc = pdf;
        for (let number = 1; number <= pdf.numPages; number += 1) {
          if (token !== this.renderToken) return;
          const page = await pdf.getPage(number);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: 140 / base.width });
          const canvas = document.createElement("canvas");
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          if (token !== this.renderToken) return;
          this.thumbs.push({ page: number - 1, src: canvas.toDataURL("image/jpeg", 0.72) });
          page.cleanup();
        }
      } catch {
        if (token === this.renderToken) this.thumbs = [];
      } finally {
        if (token === this.renderToken) this.thumbsLoading = false;
        if (token !== this.renderToken && pdf) pdf.destroy().catch(() => {});
      }
    },
    async reload() {
      this.renderToken += 1;
      this.destroyPdf();
      this.thumbs = [];
      this.thumbsLoading = false;
      this.revokeUrl();
      this.error = "";
      if (!this.filename) return;

      this.loading = true;
      try {
        const { data, headers } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/document`,
          { responseType: "blob" },
        );
        const headerType = headers["content-type"] || "";
        const blob = data instanceof Blob ? data : new Blob([data]);
        this.isPdf = headerType.includes("pdf") || blob.type.includes("pdf") || this.filename.toLowerCase().endsWith(".pdf");
        const pdfBlob = this.isPdf && !String(blob.type || headerType).includes("pdf")
          ? new Blob([blob], { type: "application/pdf" })
          : blob;
        const buffer = this.isPdf ? await pdfBlob.arrayBuffer() : null;
        this.objectUrl = URL.createObjectURL(pdfBlob);
        if (buffer) this.renderThumbs(buffer);
      } catch (e) {
        this.error = formatOcrApiError(e, "Could not load document for preview.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
